import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Country } from '@shared-features/models';
import { FunctionUtil } from '@shared/utils';
import { BaseComponent } from '../base-component/base.component';
import { SharedAPI } from '@shared-features/services';
import { MobileNumberLengthValidator, PhoneNumberValidatorWithException } from '@shared/utils/form-validators.util';
import { MobileLengthPerCode } from '@shared-features/enums/shared-enums.enum';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { LayoutService } from '@layout/services/layout.service';

@Component({
  selector: 'app-mobile-number',
  templateUrl: './mobile-number.component.html',
  styleUrls: ['./mobile-number.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => MobileNumberComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => MobileNumberComponent),
    }
  ],
})
export class MobileNumberComponent extends BaseComponent implements ControlValueAccessor, Validator, OnInit {
  form: FormGroup;
  countries: Country[];
  /** Set when exactly one country is offered; the template then shows it as a fixed prefix. */
  singleCountry: Country | null = null;
  MobileLengthPerCode = MobileLengthPerCode;
  isRtl=false;
  onChange = (value: any) => null;
  onTouched = () => null;

  constructor( private formBuilder: FormBuilder, private sharedAPI: SharedAPI,  private changeDetector: ChangeDetectorRef , private layoutService: LayoutService) {
    super();
  }

  ngOnInit(): void {
    this.layoutService.websiteDirection$.subscribe(dir => {
    this.isRtl = dir === 'rtl';
  });

  }

  /**
   * The single message to show. Format problems win over length, because the
   * length is meaningless until the format is right; `required` returns null
   * since the host form renders that one itself.
   */
  get numberErrorKey(): string | null {
    const errors = this.form?.get('number')?.errors;
    if (!errors || errors.required) {
      return null;
    }
    if (errors.whitespace || errors.invalidPhoneNumber) {
      return 'ErrorMessages.InvalidPhone';
    }
    if (errors.invalidPhoneNumberType) {
      return 'ErrorMessages.invalidPhoneNumberType';
    }
    if (errors.invalidPhoneNumberLength) {
      return 'ErrorMessages.InValidMobileLength';
    }
    return null;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.form && this.form.invalid && this.form.get('number')?.dirty) {
      const numberErrors = this.form.get('number')?.errors;
      if (numberErrors) {
        return numberErrors;
      }
      return { invalidForm: true };
    }
    return null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  writeValue(value: string): void {
    this.load(this.sharedAPI.getAllCountries(), { isLoadingTransparent: true }).subscribe((countries) => {
      this.countries = countries.filter((country: Country) => country.isoCode !== 'EG');
      this.singleCountry = this.countries.length === 1 ? this.countries[0] : null;
      this.initForm();
      if (value) {
        const mobileNumber = value.split('-');
        const code = (this.countries || []).find((country)=> country.phoneKey.toString() === mobileNumber[0].toString())?.phoneKey;
        if (code) {
          this.syncFormWithValue({ code, number: mobileNumber[1] });
        } else {
          // The code is not an offered country (e.g. a stale non-KSA draft); clear the host
          // value too, or it stays valid there and gets submitted with an empty picker.
          this.onChange(null);
        }
      }
      this.changeDetector.markForCheck();
    });
  }

  initForm(): void {
    this.form = this.formBuilder.group({
      code: [this.singleCountry?.phoneKey ?? FunctionUtil.getSaudiArabiaPhoneKey(this.countries), [Validators.required]],
      number: [
        '',
        [Validators.required, PhoneNumberValidatorWithException, MobileNumberLengthValidator(() => this.form?.value?.code)],
      ],
    });

    this.form.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(50),
    ).subscribe(() => {
      this.handleControlsChange();
    });

    this.form.controls.code.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(50),
    ).subscribe(value => {
      this.form.controls.number.setValidators([Validators.required, PhoneNumberValidatorWithException, MobileNumberLengthValidator(() => value)]);
      this.form.controls.number.updateValueAndValidity();
      this.onTouched();
    });
  }

  handleControlsChange(): void {
    const { code, number } = this.form.value;
    const mobileNumber = number?.length ? `${code}-${number}` : null;
    this.onChange(mobileNumber);
    this.onTouched();
    this.changeDetector.markForCheck();
  }

  private syncFormWithValue(value: {code: string, number: string}): void {
    this.form.setValue({...this.form.value, ...value }, { onlySelf: true , emitEvent: false });
  }
}
